import { CanvasDOMManager } from '../core/dom-managers';
import { getCanvasDocument } from '../env';
import type { CanvasObject, CanvasRenderContext } from '../core/types/canvas-object';
import type { SelectableCanvasOptions } from '../core/types/selectable-canvas-options';
import type { SelectableCanvasEvents, SelectionChangeReason } from '../core/types/selectable-canvas-events';
import { cloneMatrix, Matrix2D, Point, transformPoint, XY } from '@imagise/shared';
import { StaticCanvas } from './static-canvas';
import { InteractiveObject } from './editor-object';

type SelectionBox = {
  start: Point;
  end: Point;
};

type TouchScrollingOptions = {
  allowTouchScrolling?: boolean;
};

const DEFAULT_SELECTABLE_OPTIONS: Required<
  Pick<
    SelectableCanvasOptions,
    | 'selection'
    | 'selectionColor'
    | 'selectionBorderColor'
    | 'selectionLineWidth'
    | 'selectionDashArray'
    | 'selectionFullyContained'
    | 'hoverCursor'
    | 'moveCursor'
    | 'defaultCursor'
    | 'perPixelTargetFind'
    | 'targetFindTolerance'
    | 'skipTargetFind'
    | 'preserveObjectStacking'
  >
> = {
  selection: true,
  selectionColor: 'rgba(46,115,252,0.15)',
  selectionBorderColor: '#2e73fc',
  selectionLineWidth: 1,
  selectionDashArray: [],
  selectionFullyContained: false,
  hoverCursor: 'move',
  moveCursor: 'move',
  defaultCursor: 'default',
  perPixelTargetFind: false,
  targetFindTolerance: 0,
  skipTargetFind: false,
  preserveObjectStacking: true,
};

const toPoint = (xy: Point | XY): Point => (xy instanceof Point ? xy.clone() : new Point(xy));

const isInteractiveObject = (object: CanvasObject): object is InteractiveObject => object instanceof InteractiveObject;

export class SelectableCanvas<EventSpec extends SelectableCanvasEvents = SelectableCanvasEvents>
  extends StaticCanvas<EventSpec>
  implements SelectableCanvasOptions
{
  selection = DEFAULT_SELECTABLE_OPTIONS.selection;
  selectionColor = DEFAULT_SELECTABLE_OPTIONS.selectionColor;
  selectionBorderColor = DEFAULT_SELECTABLE_OPTIONS.selectionBorderColor;
  selectionLineWidth = DEFAULT_SELECTABLE_OPTIONS.selectionLineWidth;
  selectionDashArray = [...DEFAULT_SELECTABLE_OPTIONS.selectionDashArray];
  selectionFullyContained = DEFAULT_SELECTABLE_OPTIONS.selectionFullyContained;
  hoverCursor: CSSStyleDeclaration['cursor'] = DEFAULT_SELECTABLE_OPTIONS.hoverCursor;
  moveCursor: CSSStyleDeclaration['cursor'] = DEFAULT_SELECTABLE_OPTIONS.moveCursor;
  defaultCursor: CSSStyleDeclaration['cursor'] = DEFAULT_SELECTABLE_OPTIONS.defaultCursor;
  /**
   * When enabled (on the canvas or per object), a geometric hit is
   * confirmed by rendering the target into a scratch canvas and probing
   * pixel alpha around the pointer, so transparent areas do not grab
   * the pointer — mirrors upstream `_checkTarget`/`isTargetTransparent`.
   */
  perPixelTargetFind = DEFAULT_SELECTABLE_OPTIONS.perPixelTargetFind;
  targetFindTolerance = DEFAULT_SELECTABLE_OPTIONS.targetFindTolerance;
  skipTargetFind = DEFAULT_SELECTABLE_OPTIONS.skipTargetFind;
  preserveObjectStacking = DEFAULT_SELECTABLE_OPTIONS.preserveObjectStacking;

  private _activeObjects: InteractiveObject[] = [];
  private _selectionBox?: SelectionBox;
  private pixelFindCanvas?: HTMLCanvasElement;

  declare elements: CanvasDOMManager;

  constructor(element?: string | HTMLCanvasElement, options: SelectableCanvasOptions = {}) {
    super(element, options);
    this.applySelectableOptions(options);
  }

  get upperCanvasEl(): HTMLCanvasElement | undefined {
    return this.elements?.upper?.el;
  }

  get contextTop(): CanvasRenderingContext2D | null | undefined {
    return this.elements?.upper?.ctx;
  }

  get wrapperEl(): HTMLDivElement | undefined {
    return this.elements?.container;
  }

  override clear(): void {
    this.discardActiveObject();
    super.clear();
  }

  override setViewportTransform(transform: Matrix2D): void {
    super.setViewportTransform(transform);
    this.updateActiveObjectRenderState();
  }

  override _onObjectRemoved(object: CanvasObject, index?: number): void {
    super._onObjectRemoved(object, index);
    if (isInteractiveObject(object) && this.isObjectActive(object)) {
      this.updateActiveObjects(this._activeObjects.filter(item => item !== object));
    }
  }

  protected override renderOverlayExtras(ctx: CanvasRenderingContext2D): void {
    super.renderOverlayExtras(ctx);
    this.drawActiveObjectOverlay(ctx);
    this.drawSelectionBox(ctx);
  }

  protected override initElements(element?: string | HTMLCanvasElement, options?: SelectableCanvasOptions): void {
    this.elements = new CanvasDOMManager(element, {
      allowTouchScrolling: this.resolveAllowTouchScrolling(options),
    });
  }

  private resolveAllowTouchScrolling(options?: SelectableCanvasOptions): boolean {
    const optionsValue = options as TouchScrollingOptions | undefined;
    if (typeof optionsValue?.allowTouchScrolling === 'boolean') {
      return optionsValue.allowTouchScrolling;
    }
    const instanceValue = this as TouchScrollingOptions;
    if (typeof instanceValue.allowTouchScrolling === 'boolean') {
      return instanceValue.allowTouchScrolling;
    }
    return false;
  }

  getActiveObject(): InteractiveObject | undefined {
    return this._activeObjects[0];
  }

  getActiveObjects(): InteractiveObject[] {
    return [...this._activeObjects];
  }

  setActiveObject(object?: CanvasObject, reason: SelectionChangeReason = 'api'): InteractiveObject | undefined {
    if (!object) {
      this.updateActiveObjects([], reason);
      return undefined;
    }
    if (!isInteractiveObject(object)) {
      return undefined;
    }
    this.updateActiveObjects([object], reason);
    return object;
  }

  selectObjects(objects: CanvasObject[], reason: SelectionChangeReason = 'api'): InteractiveObject[] {
    const interactive = objects.filter(isInteractiveObject);
    this.updateActiveObjects(interactive, reason);
    return this.getActiveObjects();
  }

  discardActiveObject(reason: SelectionChangeReason = 'api'): this is { _activeObjects: [] } {
    if (!this._activeObjects.length) {
      return true;
    }
    this.updateActiveObjects([], reason);
    return true;
  }

  findTarget(point: Point | XY): InteractiveObject | undefined {
    if (this.skipTargetFind) {
      return undefined;
    }
    const scenePoint = toPoint(point);
    const candidates = this.getSelectionStack();
    for (let i = candidates.length - 1; i >= 0; i -= 1) {
      const candidate = candidates[i];
      if (!candidate.visible || !candidate.selectable) {
        continue;
      }
      if (this.isPointInTarget(candidate, scenePoint)) {
        return candidate;
      }
    }
    return undefined;
  }

  selectObjectAtPoint(point: Point | XY, reason: SelectionChangeReason = 'pointer'): InteractiveObject | undefined {
    const target = this.findTarget(point);
    if (target) {
      this.setActiveObject(target, reason);
      return target;
    }
    this.discardActiveObject(reason);
    return undefined;
  }

  beginGroupSelection(start: Point | XY): void {
    this._selectionBox = {
      start: toPoint(start),
      end: toPoint(start),
    };
    this.requestRenderAll('manual');
  }

  updateGroupSelection(point: Point | XY): void {
    if (!this._selectionBox) {
      return;
    }
    this._selectionBox.end = toPoint(point);
    this.requestRenderAll('manual');
  }

  finalizeGroupSelection(
    options: {
      addToSelection?: boolean;
      reason?: SelectionChangeReason;
    } = {}
  ): InteractiveObject[] {
    const box = this._selectionBox;
    this._selectionBox = undefined;
    if (!box) {
      return [];
    }
    const objects = this.collectSelectableInRect(box.start, box.end);
    const reason = options.reason ?? 'group';
    if (options.addToSelection && this._activeObjects.length) {
      const merged = [...this._activeObjects, ...objects];
      this.updateActiveObjects(merged, reason);
    } else {
      this.updateActiveObjects(objects, reason);
    }
    this.requestRenderAll('manual');
    return objects;
  }

  protected applySelectableOptions(options: SelectableCanvasOptions = {}): void {
    if (typeof options.selection === 'boolean') {
      this.selection = options.selection;
    }
    if (typeof options.selectionColor === 'string') {
      this.selectionColor = options.selectionColor;
    }
    if (typeof options.selectionBorderColor === 'string') {
      this.selectionBorderColor = options.selectionBorderColor;
    }
    if (typeof options.selectionLineWidth === 'number') {
      this.selectionLineWidth = options.selectionLineWidth;
    }
    if (Array.isArray(options.selectionDashArray)) {
      this.selectionDashArray = [...options.selectionDashArray];
    }
    if (typeof options.selectionFullyContained === 'boolean') {
      this.selectionFullyContained = options.selectionFullyContained;
    }
    if (typeof options.hoverCursor === 'string') {
      this.hoverCursor = options.hoverCursor;
    }
    if (typeof options.moveCursor === 'string') {
      this.moveCursor = options.moveCursor;
    }
    if (typeof options.defaultCursor === 'string') {
      this.defaultCursor = options.defaultCursor;
    }
    if (typeof options.perPixelTargetFind === 'boolean') {
      this.perPixelTargetFind = options.perPixelTargetFind;
    }
    if (typeof options.targetFindTolerance === 'number') {
      this.targetFindTolerance = options.targetFindTolerance;
    }
    if (typeof options.skipTargetFind === 'boolean') {
      this.skipTargetFind = options.skipTargetFind;
    }
    if (typeof options.preserveObjectStacking === 'boolean') {
      this.preserveObjectStacking = options.preserveObjectStacking;
    }
  }

  private getSelectionStack(): InteractiveObject[] {
    const stack = (this.objects as CanvasObject[]).filter(isInteractiveObject);
    if (this.preserveObjectStacking) {
      return stack;
    }
    const active = this.getActiveObject();
    if (!active) {
      return stack;
    }
    const nextStack = stack.filter(object => object !== active);
    nextStack.push(active);
    return nextStack;
  }

  private isPointInTarget(object: InteractiveObject, point: Point): boolean {
    if (!this.isPointWithinTargetBounds(object, point)) {
      return false;
    }
    if (!(this.perPixelTargetFind || object.perPixelTargetFind)) {
      return true;
    }
    return !this.isTargetTransparent(object, point);
  }

  private isPointWithinTargetBounds(object: InteractiveObject, point: Point): boolean {
    if (object.containsPoint(point)) {
      return true;
    }
    if (!this.targetFindTolerance) {
      return false;
    }
    const tolerance = this.targetFindTolerance;
    const tl = new Point(point.x - tolerance, point.y - tolerance);
    const br = new Point(point.x + tolerance, point.y + tolerance);
    return object.intersectsWithRect(tl, br);
  }

  /**
   * Renders the target alone into a small scratch canvas centered on the
   * pointer and reports whether every pixel inside the targetFindTolerance
   * window is fully transparent. Without a readable 2d context (e.g. SSR)
   * the geometric hit is kept.
   */
  isTargetTransparent(target: InteractiveObject, scenePoint: Point): boolean {
    const tolerance = Math.max(0, Math.round(this.targetFindTolerance));
    const size = tolerance * 2 + 1;
    const ctx = this.getPixelFindContext(size);
    if (!ctx) {
      return false;
    }

    const viewportPoint = transformPoint(scenePoint, this.viewportTransform);
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(-viewportPoint.x + tolerance, -viewportPoint.y + tolerance);
    const [a, b, c, d, e, f] = this.viewportTransform;
    ctx.transform(a, b, c, d, e, f);
    target.render(ctx, this.createPixelFindRenderContext());
    ctx.restore();

    const { data } = ctx.getImageData(0, 0, size, size);
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] > 0) {
        return false;
      }
    }
    return true;
  }

  private getPixelFindContext(size: number): CanvasRenderingContext2D | null {
    if (!this.pixelFindCanvas) {
      this.pixelFindCanvas = getCanvasDocument().createElement('canvas');
    }
    if (this.pixelFindCanvas.width !== size || this.pixelFindCanvas.height !== size) {
      this.pixelFindCanvas.width = size;
      this.pixelFindCanvas.height = size;
    }
    return this.pixelFindCanvas.getContext('2d', { willReadFrequently: true });
  }

  private createPixelFindRenderContext(): CanvasRenderContext {
    return {
      viewportTransform: cloneMatrix(this.viewportTransform),
      retinaScaling: 1,
      width: this.width,
      height: this.height,
      skipOffscreen: false,
    };
  }

  private collectSelectableInRect(start: Point, end: Point): InteractiveObject[] {
    const tl = new Point(Math.min(start.x, end.x), Math.min(start.y, end.y));
    const br = new Point(Math.max(start.x, end.x), Math.max(start.y, end.y));
    return (this.objects as CanvasObject[]).reduce<InteractiveObject[]>((acc, object) => {
      if (!isInteractiveObject(object) || !object.visible || !object.selectable) {
        return acc;
      }
      const matches = this.selectionFullyContained
        ? object.isContainedWithinRect(tl, br)
        : object.intersectsWithRect(tl, br);
      if (matches) {
        acc.push(object);
      }
      return acc;
    }, []);
  }

  private drawActiveObjectOverlay(ctx: CanvasRenderingContext2D): void {
    // `selection` gates only interactive drag-box selection (drawSelectionBox);
    // a programmatically activated object still renders its controls —
    // matches upstream behavior.
    if (this._activeObjects.length === 0) {
      return;
    }
    this.updateActiveObjectRenderState();
    this._activeObjects.forEach(object => {
      object.drawSelectionBackground(ctx);
    });
    this._activeObjects.forEach(object => {
      object._renderControls(ctx);
    });
  }

  private drawSelectionBox(ctx: CanvasRenderingContext2D): void {
    if (!this.selection || !this._selectionBox) {
      return;
    }
    const { start, end } = this._selectionBox;
    const startViewport = transformPoint(start, this.viewportTransform);
    const endViewport = transformPoint(end, this.viewportTransform);
    const minX = Math.min(startViewport.x, endViewport.x);
    const minY = Math.min(startViewport.y, endViewport.y);
    const maxX = Math.max(startViewport.x, endViewport.x);
    const maxY = Math.max(startViewport.y, endViewport.y);

    ctx.save();
    if (this.selectionColor) {
      ctx.fillStyle = this.selectionColor;
      ctx.fillRect(minX, minY, maxX - minX, maxY - minY);
    }
    if (this.selectionLineWidth && this.selectionBorderColor) {
      ctx.lineWidth = this.selectionLineWidth;
      ctx.strokeStyle = this.selectionBorderColor;
      if (ctx.setLineDash) {
        ctx.setLineDash(this.selectionDashArray);
      }
      ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
    }
    ctx.restore();
  }

  setCursor(cursor: CSSStyleDeclaration['cursor']): void {
    const canvas = this.upperCanvasEl ?? this.getElement();
    if (canvas?.style) {
      canvas.style.cursor = cursor || 'default';
    }
  }

  private updateActiveObjects(objects: InteractiveObject[], reason: SelectionChangeReason = 'api'): void {
    const filtered = this.filterSelectableObjects(objects);
    if (this.areSameSelection(filtered)) {
      return;
    }
    const previous = this.getActiveObjects();
    // A truthy return from onDeselect/onSelect vetoes the change for that
    // object (upstream contract).
    const kept: InteractiveObject[] = [];
    previous.forEach(object => {
      if (!filtered.includes(object)) {
        if (object.onDeselect()) {
          kept.push(object);
          return;
        }
        object.isActive = false;
      }
    });
    const next: InteractiveObject[] = [];
    filtered.forEach(object => {
      if (!previous.includes(object)) {
        if (object.onSelect()) {
          return;
        }
        object.isActive = true;
      }
      object.setCoords();
      next.push(object);
    });
    const selectionResult = [...kept, ...next];
    this._activeObjects = selectionResult;
    this.updateActiveObjectRenderState();
    this.fireSelectionEvents(previous, selectionResult, reason);
    this.requestRenderAll('manual');
  }

  private filterSelectableObjects(objects: InteractiveObject[]): InteractiveObject[] {
    const unique: InteractiveObject[] = [];
    objects.forEach(object => {
      if (!this.contains(object)) {
        return;
      }
      if (!object.visible || !object.selectable) {
        return;
      }
      if (!unique.includes(object)) {
        unique.push(object);
      }
    });
    return unique;
  }

  private areSameSelection(next: InteractiveObject[]): boolean {
    if (next.length !== this._activeObjects.length) {
      return false;
    }
    return next.every(object => this._activeObjects.includes(object));
  }

  private fireSelectionEvents(
    previous: InteractiveObject[],
    next: InteractiveObject[],
    reason: SelectionChangeReason
  ): void {
    const timestamp = Date.now();
    if (!previous.length && !next.length) {
      return;
    }
    if (!previous.length && next.length) {
      this.fire(
        'selection:created' as keyof EventSpec,
        {
          selected: next,
          timestamp,
          reason,
        } as EventSpec[keyof EventSpec]
      );
      return;
    }
    if (previous.length && !next.length) {
      const detail = {
        deselected: previous,
        timestamp,
        reason,
      } as EventSpec[keyof EventSpec];
      this.fire('before:selection:cleared' as keyof EventSpec, detail);
      this.fire('selection:cleared' as keyof EventSpec, detail);
      return;
    }
    const added = next.filter(object => !previous.includes(object));
    const removed = previous.filter(object => !next.includes(object));
    if (added.length || removed.length) {
      this.fire(
        'selection:updated' as keyof EventSpec,
        {
          selected: added,
          deselected: removed,
          timestamp,
          reason,
        } as EventSpec[keyof EventSpec]
      );
    }
  }

  private updateActiveObjectRenderState(): void {
    const retinaScaling = this.getRetinaScaling();
    this._activeObjects.forEach(object => {
      object.setViewportTransform(this.viewportTransform);
      object.setRetinaScaling(retinaScaling);
    });
  }

  private isObjectActive(object: InteractiveObject): boolean {
    return this._activeObjects.includes(object);
  }
}
